<!-- Workflow panel -->
@if ((isQuarterCloseEnabled$ | async) && (isClosingPanelEnabled$ | async)) {
  <aux-workflow-panel
    className="mb-7"
    [workflowName]="workflowName"
    [processing]="(iCloseMonthsProcessing$ | async)!"
    [hasUserPermissions]="userHasLockForecastMethodologyPermission"
  />
}

@if ((categoryQuery.selectLoading() | async) === true || isQuickForecastProcessing() === true) {
  <div class="spinner m-auto mt-40 h-32 w-32 border-8"></div>
}

<ng-template #vendorDropdown>
  <div class="flex items-center">
    <div class="mr-2 text-sm">Vendor Contract</div>
    <aux-vendor-dropdown
      [vendors]="(organizationQuery.allVendors$ | async)!"
      [formControl]="selectedVendor"
      [beforeSelect]="beforeSelectVendor"
      (onChange)="onOrganizationSelected($event)"
    />
  </div>
</ng-template>

@if ((categoryQuery.selectLoading() | async) === false && isQuickForecastProcessing() !== true) {
  @if ({ manual: isSelectedVendorHasManualForecast$ | async }; as obj) {
    <!-- Manual forecast container -->
    @if (obj.manual === true) {
      <ng-container [ngTemplateOutlet]="vendorDropdown" />
      <div class="mt-28 flex flex-col items-center justify-center space-y-5 text-aux-gray-dark">
        <aux-icon name="FileDescription" />
        <div class="text-xl font-bold">Selected Vendor Contract has a manual forecast</div>
      </div>
    }

    <!-- Non-manual container -->
    @if (obj.manual === false) {
      @if (showAnalyticsSection$ | async) {
        <aux-forecast-analytics-section />
      }

      @if ((categoryQuery.selectLoading() | async) === false) {
        <!-- Driver links -->
        @if ((successForecast$ | async) === false) {
          <div id="notSuccessForecastMessage">
            <div class="mt-8 flex items-start bg-aux-warn p-3">
              <strong class="mr-1">Drivers Not Set:</strong>
              Drivers have not been added.
              @if (isPatientDriversLinkVisible) {
                <a
                  class="ml-1 font-bold text-aux-blue-light-200"
                  [routerLink]="[patientDriversLink]"
                >
                  Add patient driver now.
                </a>
              }

              @if (isSiteDriversLinkVisible) {
                <a class="ml-3 font-bold text-aux-blue-light-200" [routerLink]="[siteDriversLink]">
                  Add site driver now.
                </a>
              }
            </div>
          </div>
        }

        <div class="mb-8 mt-8 flex items-center justify-between text-xs">
          <div class="flex items-center space-x-6">
            <ng-container [ngTemplateOutlet]="vendorDropdown" />
            <aux-checkbox
              id="hide-activities-with-no-remaining-cost"
              [checked]="(categoryStore.hideActivitiesWithNoRemainingCost$ | async)!"
              (customChange)="onHideActivitiesWithNoRemainingCost($event)"
            >
              Hide Activities with
              <br />
              No Remaining Costs
            </aux-checkbox>
            <aux-checkbox
              id="show-only-unforecasted-activities"
              [checked]="(categoryStore.hideForecastedActivities$ | async)!"
              (customChange)="onShowOnlyUnforecasted($event)"
            >
              Show Only
              <br />
              Unforecasted Activities
            </aux-checkbox>
          </div>

          <div class="flex items-center space-x-2">
            <aux-button
              variant="secondary"
              label="Apply Drivers in Bulk"
              classList="whitespace-nowrap"
              icon="CirclePlus"
              [disabled]="
                (isForecastFinalized$ | async) || !hasModifyForecastMethodologyPermission()
              "
              (click)="openQuickForecastModal()"
              [auxTooltip]="
                lockForecastMethodologyTooltip() || noModifyForecastMethodologyPermissionTooltip()
              "
            />
            <aux-button
              variant="secondary"
              icon="Eye"
              label="Forecast"
              (click)="navigateToBudgetForecast()"
            />
            <aux-export-excel-button
              className="ml-1"
              [clickFN]="onDownloadForecastSettings.bind(this)"
              [spinnerSize]="7"
              pendoTag="download-forecast-settings-excel-export-button"
            />
          </div>
        </div>

        <!-- Forecast table -->
        <aux-forecast-table-grid
          [isForecastFinalized]="(isForecastFinalized$ | async) || false"
          [isClosedMonthsProcessing]="(iCloseMonthsProcessing$ | async) || false"
          [isPatientDriverAvailable]="isPatientDriverAvailable || false"
          [isPatientDriversLinkVisible]="isPatientDriversLinkVisible || false"
          [isSiteDriverAvailable]="isSiteDriverAvailable || false"
          [isCustomCurveAvailable]="true"
          [isSiteDriversLinkVisible]="isSiteDriversLinkVisible || false"
          [selectedVendor]="selectedVendor"
          [successForecast]="(successForecast$ | async) || false"
          [changeDiscountTotalAmount]="changeDiscountTotalAmount"
          [changeDiscountTotalPercent]="changeDiscountTotalPercent"
          [saveCheck]="(saveCheck$ | async) || false"
          (emitSaveCheck)="onSaveCheck($event)"
          (emitIsPatientDriversLinkVisible)="onIsPatientDriversLinkVisible($event)"
          (emitIsSiteDriversLinkVisible)="onIsSiteDriversLinkVisible($event)"
          (emitHideDiscounts)="onHideDiscounts($event)"
          (emitSuccessForecast)="onSuccessForecast($event)"
          (emitChangeDiscountTotalAmount)="onChangeDiscountTotalAmount($event)"
          (emitChangeDiscountTotalPercent)="onChangeDiscountTotalPercent($event)"
          (emitChangeDiscountTotalAmountEnable)="onChangeDiscountTotalAmountEnable($event)"
          (emitChangeDiscountTotalPercentEnable)="onChangeDiscountTotalPercentEnable($event)"
          (gridValueChange)="onGridValueChange($event)"
        />

        <!-- Discount section -->
        @if ((categoryQuery.selectTopCategories$ | async)?.length) {
          @if ((hideDiscounts$ | async) === false) {
            <div
              class="z-10 mb-1 grid h-16 grid-cols-12 items-center gap-5 rounded-md border bg-aux-gray-light px-8 py-1 text-xs font-medium"
              [ngClass]="{ 'mb-20': (saveCheck$ | async) || false }"
            >
              <div class="col-span-2 text-base font-bold">
                <div class="flex h-full items-center justify-between text-xs font-semibold">
                  Services Discount
                </div>
              </div>
              <div class="col-span-3 grid grid-cols-2 justify-items-end text-xs font-semibold">
                @if (
                  discountType$.getValue() === 'DISCOUNT_PERCENTAGE' ||
                  discountType$.getValue() === 'DISCOUNT_TOTAL'
                ) {
                  <span class="mr-2 w-24">Percentage:</span>
                  <span class="min-w-[70px] text-right">
                    {{ changeDiscountTotalPercent.value.toFixed(2) }}%
                  </span>
                }
                @if (
                  discountType$.getValue() === 'DISCOUNT_TOTAL' &&
                  changeDiscountTotalAmount.value !== 0 &&
                  changeDiscountTotalAmount.value !== null
                ) {
                  <span class="mr-2 w-24">Total:</span>
                  <span class="min-w-[70px] text-right">
                    {{ changeDiscountTotalAmount.value | money: orgCurrency }}
                  </span>
                }
              </div>
            </div>
          }
        }

        <!-- No results container -->
        @if (
          !(topLevelActivities$ | async)?.length &&
          !(categoryQuery.selectTopCategories$ | async)?.length
        ) {
          @if (
            (topLevelActivities$ | async)?.length || (categoryQuery.topCategories$ | async)?.length
          ) {
            No Results found with your selection. Please try
            <span class="aux-link cursor-pointer" (click)="clearFilters()">
              clearing the filters
            </span>
            .
          } @else {
            <p>No budget data found for this Vendor Contract</p>
          }
        }
      }

      <!-- Save changes -->
      @if ((saveCheck$ | async) || false) {
        <aux-save-changes
          [onSaveChanges]="forecastTableGridComponent.saveChanges"
          [showDiscardChangesBtn]="true"
          [saveChangesDisabled]="hasUnforecastedCustomDrivers()"
          [saveChangesTooltip]="saveChangesTooltip()"
          (cancel)="forecastTableGridComponent.onDiscardChanges()"
        />
      }
    }
  }
}
